import { useMemo, useEffect } from 'react';
import { useLocation } from '@tanstack/react-router';
import { atom, useAtom, useAtomValue } from 'jotai';

export interface BreadcrumbItem {
  title: string;
  url: string;
  isHome?: boolean;
  isCurrent?: boolean;
}

// リソース名を保持するAtom（軽量な状態管理）
const resourceTitleAtom = atom<string | undefined>(undefined);

// 静的なパンくず設定（パスパターンごと）
const BREADCRUMB_CONFIG: Record<
  string,
  (pathname: string, search?: Record<string, unknown>, resourceTitle?: string) => BreadcrumbItem[]
> = {
  // トップページ
  '^/agent$': () => [{ title: 'トップ', url: '/agent', isHome: true }],

  // 一覧ページ
  '^/agent/candidates$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
  ],
  '^/agent/companies$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '企業管理', url: '/agent/companies' },
  ],
  '^/agent/provided_job_posts$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
  ],
  '^/agent/analysis$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '分析', url: '/agent/analysis' },
  ],
  '^/agent/shared_job_posts$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/shared_job_posts' },
  ],
  '^/agent/provided_job_posts/new/manual$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: '手動で作成', url: '/agent/provided_job_posts/new/manual' },
  ],
  '^/agent/provided_job_posts/new/pdf$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'PDFから作成', url: '/agent/provided_job_posts/new/pdf' },
  ],
  '^/agent/job_change_processes$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: 'マッチング管理', url: '/agent/job_change_processes' },
  ],
  '^/agent/profits$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '売上管理', url: '/agent/profits' },
  ],
  '^/agent/outer_job_posts$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '外部連携求人', url: '/agent/outer_job_posts' },
  ],
  '^/agent/linked_accounts$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '連携先管理', url: '/agent/linked_accounts' },
  ],
  '^/agent/linked_accounts/new$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '連携先管理', url: '/agent/linked_accounts' },
    { title: '連携先追加', url: '/agent/linked_accounts/new' },
  ],
  '^/agent/linked_accounts/new/group/[^/]+$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '連携先管理', url: '/agent/linked_accounts' },
    { title: '連携先追加', url: '/agent/linked_accounts/new' },
  ],
  '^/agent/linked_accounts/new/[^/]+$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '連携先管理', url: '/agent/linked_accounts' },
    { title: '連携先追加', url: '/agent/linked_accounts/new' },
  ],
  '^/agent/linked_accounts/shared$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '連携先管理', url: '/agent/linked_accounts' },
    { title: '求人データベース', url: '/agent/linked_accounts/shared' },
  ],
  '^/agent/linked_accounts/\\d+$': (pathname) => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '連携先管理', url: '/agent/linked_accounts' },
    { title: '連携先詳細', url: pathname },
  ],
  '^/agent/export_requests$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: 'データ出力', url: '/agent/export_requests' },
  ],
  '^/agent/candidate_mails$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: 'メール管理', url: '/agent/candidate_mails' },
  ],
  '^/agent/candidate_mails/new$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: 'メール管理', url: '/agent/candidate_mails' },
    { title: 'メール作成', url: '/agent/candidate_mails/new' },
  ],
  '^/agent/candidate_mails/templates/new$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: 'メール管理', url: '/agent/candidate_mails' },
    { title: 'テンプレート作成', url: '/agent/candidate_mails/templates/new' },
  ],
  '^/agent/candidate_mails/templates/\\d+/edit$': (pathname) => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: 'メール管理', url: '/agent/candidate_mails' },
    { title: 'テンプレート編集', url: pathname },
  ],
  '^/agent/settings$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '共通設定', url: '/agent/settings' },
  ],
  '^/agent/form$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '登録フォーム', url: '/agent/form' },
  ],
  '^/agent/form/settings$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '登録フォーム', url: '/agent/form' },
    { title: '基本設定', url: '/agent/form/settings' },
  ],

  // カスタマイズ系（統一SPA）
  '^/agent/customize$': (_pathname, search) => {
    // TanStack Routerのsearchオブジェクトからtypeを取得
    const type = search?.type as string | undefined;

    switch (type) {
      case 'candidate_profile':
        return [
          { title: 'トップ', url: '/agent', isHome: true },
          { title: '求職者管理', url: '/agent/candidates' },
          { title: '求職者カスタム', url: '/agent/customize?type=candidate_profile' },
        ];
      case 'company':
        return [
          { title: 'トップ', url: '/agent', isHome: true },
          { title: '企業管理', url: '/agent/companies' },
          { title: 'カスタマイズ', url: '/agent/customize?type=company' },
        ];
      case 'candidate_interview_log':
        return [
          { title: 'トップ', url: '/agent', isHome: true },
          { title: '求職者管理', url: '/agent/candidates' },
          { title: 'カスタマイズ', url: '/agent/customize?type=candidate_interview_log' },
        ];
      default:
        return [
          { title: 'トップ', url: '/agent', isHome: true },
          { title: '求職者管理', url: '/agent/candidates' },
          { title: 'カスタマイズ', url: '/agent/customize?type=candidate_profile' },
        ];
    }
  },

  // タグ管理
  '^/agent/candidate_profile_tags$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
    { title: 'タグ管理', url: '/agent/candidate_profile_tags' },
  ],
  '^/agent/candidate_tags$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
    { title: 'タグ管理', url: '/agent/candidate_tags' },
  ],
  '^/agent/company_tags$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '企業管理', url: '/agent/companies' },
    { title: '企業タグ管理', url: '/agent/company_tags' },
  ],
  '^/agent/job_post_tags$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: '求人タグ管理', url: '/agent/job_post_tags' },
  ],
  '^/agent/provided_job_post_lists$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'リスト', url: '/agent/provided_job_post_lists' },
  ],
  '^/agent/provided_job_post_lists/[^/]+$': (_pathname, _search, resourceTitle?: string) => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'リスト管理', url: '/agent/provided_job_post_lists' },
    { title: resourceTitle || '読み込み中...', url: _pathname },
  ],

  // 設定系
  '^/agent/api_keys$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '共通設定', url: '/agent/settings' },
    { title: 'API管理', url: '/agent/api_keys' },
  ],
  '^/agent/organization_notification_configurations$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '共通設定', url: '/agent/settings' },
    { title: '通知設定', url: '/agent/organization_notification_configurations' },
  ],
  '^/agent/portal_configurations$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '共通設定', url: '/agent/settings' },
    { title: 'ポータルページ設定', url: '/agent/portal_configurations' },
  ],

  // インポート系
  '^/agent/candidate_import_requests$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
    { title: 'CSVから取り込み', url: '/agent/candidate_import_requests' },
  ],
  '^/agent/provided_job_posts/new/csv/?$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'CSVから取り込み', url: '/agent/provided_job_posts/new/csv' },
  ],
  '^/agent/provided_job_posts/new/csv/[^/]+$': (_pathname, _search, resourceTitle?: string) => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: resourceTitle || 'CSVから取り込み', url: _pathname },
  ],
  '^/agent/provided_job_posts/new/pdf/?$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'PDFから取り込み', url: '/agent/provided_job_posts/new/pdf' },
  ],
  '^/agent/provided_job_posts/new/pdf/[^/]+$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'PDFから取り込み', url: '/agent/provided_job_posts/new/pdf' },
  ],
  '^/agent/provided_job_posts/new/text/?$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'テキストから取り込み', url: '/agent/provided_job_posts/new/text' },
  ],
  '^/agent/provided_job_posts/new/text/[^/]+$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求人管理', url: '/agent/provided_job_posts' },
    { title: 'テキストから取り込み', url: '/agent/provided_job_posts/new/text' },
  ],
  '^/agent/csv_templates$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
    { title: 'CSVから取り込み', url: '/agent/candidate_import_requests' },
    { title: '取り込み設定', url: '/agent/csv_templates' },
  ],
  '^/agent/candidates/candidate_import_requests/new$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
    { title: 'CSVから取り込み', url: '/agent/candidates/candidate_import_requests/new' },
  ],
  '^/agent/candidates/candidate_import_requests/new/[^/]+$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
    { title: 'CSVから取り込み', url: '/agent/candidates/candidate_import_requests/new' },
    { title: '取り込みの詳細', url: window.location.pathname },
  ],
  '^/agent/candidates/candidate_import_requests/presets/[^/]+$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '求職者管理', url: '/agent/candidates' },
    { title: 'CSVから取り込み', url: '/agent/candidates/candidate_import_requests/new' },
  ],

  // アライアンス
  '^/agent/alliance$': (_pathname, search) => {
    const tab = typeof search?.tab === 'string' ? search.tab : 'applications';

    const baseUrl = '/agent/alliance';
    const isSettingsView = tab === 'settings' || tab === 'agents';

    const items: BreadcrumbItem[] = [
      { title: 'トップ', url: '/agent', isHome: true },
      { title: 'アライアンス', url: baseUrl },
    ];

    if (isSettingsView) {
      items.push({ title: '設定・管理', url: `${baseUrl}?tab=${tab}` });
    }

    return items;
  },
  '^/agent/alliance/applications/[^/]+$': (pathname, _search, resourceTitle) => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: 'アライアンス', url: '/agent/alliance' },
    { title: resourceTitle || '読み込み中...', url: pathname },
  ],

  // 組織設定
  '^/agent/organizations$': () => [
    { title: 'トップ', url: '/agent', isHome: true },
    { title: '設定', url: '/agent/organizations' },
  ],
};

// リソースタイプと親ページのマッピング
const RESOURCE_PARENT_MAP: Record<string, { title: string; url: string }> = {
  candidates: { title: '求職者管理', url: '/agent/candidates' },
  companies: { title: '企業管理', url: '/agent/companies' },
  provided_job_posts: { title: '求人管理', url: '/agent/provided_job_posts' },
  shared_job_posts: { title: '求人データベース', url: '/agent/shared_job_posts' },
  job_change_processes: { title: 'マッチング管理', url: '/agent/job_change_processes' },
  profits: { title: '売上管理', url: '/agent/profits' },
  outer_job_posts: { title: '外部連携求人', url: '/agent/outer_job_posts' },
  job_sources: { title: '連携先管理', url: '/agent/linked_accounts' },
  export_requests: { title: 'データ出力', url: '/agent/export_requests' },
};

/**
 * パスから動的なパンくずリストを生成
 */
const generateDynamicBreadcrumb = (pathname: string, resourceTitle?: string): BreadcrumbItem[] => {
  // 詳細ページ: /agent/{resource}/{id}
  const detailMatch = pathname.match(/^\/agent\/([^/]+)\/(\d+)$/);
  if (detailMatch) {
    const [, resourceType, id] = detailMatch;
    const parent = RESOURCE_PARENT_MAP[resourceType];
    if (parent) {
      return [
        { title: 'トップ', url: '/agent', isHome: true },
        { title: parent.title, url: parent.url },
        {
          title: resourceTitle || '読み込み中...',
          url: `/agent/${resourceType}/${id}`,
        },
      ];
    }
  }

  // 編集ページ: /agent/{resource}/{id}/edit
  const editMatch = pathname.match(/^\/agent\/([^/]+)\/(\d+)\/edit$/);
  if (editMatch) {
    const [, resourceType, id] = editMatch;
    const parent = RESOURCE_PARENT_MAP[resourceType];
    if (parent) {
      return [
        { title: 'トップ', url: '/agent', isHome: true },
        { title: parent.title, url: parent.url },
        {
          title: resourceTitle || '読み込み中...',
          url: `/agent/${resourceType}/${id}`,
        },
        { title: '編集', url: pathname },
      ];
    }
  }

  // 新規作成ページ: /agent/{resource}/new
  const newMatch = pathname.match(/^\/agent\/([^/]+)\/new$/);
  if (newMatch) {
    const [, resourceType] = newMatch;
    const parent = RESOURCE_PARENT_MAP[resourceType];
    if (parent) {
      return [
        { title: 'トップ', url: '/agent', isHome: true },
        { title: parent.title, url: parent.url },
        { title: '新規作成', url: pathname },
      ];
    }
  }

  // サブリソースの新規作成: /agent/{resource}/{id}/{sub_resource}/new
  const subNewMatch = pathname.match(/^\/agent\/([^/]+)\/(\d+)\/([^/]+)\/new$/);
  if (subNewMatch) {
    const [, resourceType, id, subResource] = subNewMatch;
    const parent = RESOURCE_PARENT_MAP[resourceType];
    if (parent) {
      const subLabels: Record<string, string> = {
        job_post_endpoints: '求人追加',
        interview_logs: '面談情報の作成',
      };
      const subLabel = subLabels[subResource] ?? '新規作成';
      return [
        { title: 'トップ', url: '/agent', isHome: true },
        { title: parent.title, url: parent.url },
        {
          title: resourceTitle || '読み込み中...',
          url: `/agent/${resourceType}/${id}`,
        },
        { title: subLabel, url: pathname },
      ];
    }
  }

  // サブリソースのタブページ: /agent/{resource}/{id}/{tab}
  const tabMatch = pathname.match(/^\/agent\/([^/]+)\/(\d+)\/([^/]+)$/);
  if (tabMatch) {
    const [, resourceType, id] = tabMatch;
    const parent = RESOURCE_PARENT_MAP[resourceType];
    if (parent) {
      return [
        { title: 'トップ', url: '/agent', isHome: true },
        { title: parent.title, url: parent.url },
        {
          title: resourceTitle || '読み込み中...',
          url: `/agent/${resourceType}/${id}`,
        },
      ];
    }
  }

  return [];
};

/**
 * 各ページでリソース名を設定するためのフック
 */
export const useSetBreadcrumbTitle = (resourceTitle?: string) => {
  const [, setTitle] = useAtom(resourceTitleAtom);

  useEffect(() => {
    setTitle(resourceTitle);

    return () => {
      // アンマウント時にタイトルをクリアして、同一パスでの search 切り替えでも表示がズレないようにする
      setTitle(undefined);
    };
  }, [resourceTitle, setTitle]);
};

/**
 * パンくずリストを取得するフック（Headerで使用）
 */
export const useBreadcrumb = (): { breadcrumbItems: BreadcrumbItem[] } => {
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search as Record<string, unknown>;
  const resourceTitle = useAtomValue(resourceTitleAtom);

  const breadcrumbItems = useMemo(() => {
    // 正規化（末尾のスラッシュを削除）
    const normalizedPath =
      pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;

    // 静的な設定から検索
    for (const [pattern, generator] of Object.entries(BREADCRUMB_CONFIG)) {
      const regex = new RegExp(pattern);
      if (regex.test(normalizedPath)) {
        const items = generator(normalizedPath, search, resourceTitle);
        // 最後の要素に isCurrent を追加
        if (items.length > 0) {
          items[items.length - 1].isCurrent = true;
        }
        return items;
      }
    }

    // 動的なパンくずを生成
    const dynamicItems = generateDynamicBreadcrumb(normalizedPath, resourceTitle);
    if (dynamicItems.length > 0) {
      dynamicItems[dynamicItems.length - 1].isCurrent = true;
      return dynamicItems;
    }

    // デフォルト: トップページのみ
    return [{ title: 'トップ', url: '/agent', isHome: true, isCurrent: true }];
  }, [pathname, search, resourceTitle]);

  // パスが変わったらリソースタイトルをクリア
  useEffect(() => {
    return () => {
      // クリーンアップ時にタイトルをリセット
    };
  }, [pathname]);

  return { breadcrumbItems };
};
